class PdpSwiperGallery {
  constructor(root) {
    this.root = root;
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this._bootstrapped = false;
    this.els = {};
  }

  getTrackpadSwipeParams(enabled) {
    if (!enabled) {
      return {
        simulateTouch: false,
        allowTouchMove: false,
        grabCursor: false,
        mousewheel: false,
      };
    }

    const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    return {
      touchEventsTarget: 'container',
      simulateTouch: false,
      allowTouchMove: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      mousewheel: isFinePointer
        ? {
            enabled: true,
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
            thresholdDelta: 30,
            thresholdTime: 450,
          }
        : false,
    };
  }

  bootstrap() {
    if (this._bootstrapped) return;

    if (typeof Swiper === 'undefined') {
      requestAnimationFrame(() => this.bootstrap());
      return;
    }

    this._bootstrapped = true;
    this.cacheElements();
    this.initSwipers();
    this.setupAutoPlay();
  }

  refreshElements() {
    this.destroySwipers();
    this._bootstrapped = false;
    this.bootstrap();
  }

  cacheElements() {
    this.els = {
      liveRegion: this.root.querySelector('[id^="GalleryStatus"]'),
      mainEl: this.root.querySelector('.pdp-swiper-gallery__main'),
      thumbsEl: this.root.querySelector('.pdp-swiper-gallery__thumbs'),
      mediaList: this.root.querySelector('[data-pdp-gallery-main]'),
      thumbTrack: this.root.querySelector('[data-pdp-gallery-thumbs]'),
      navPrev: this.root.querySelector('.js-product-media-navigation-prev'),
      navNext: this.root.querySelector('.js-product-media-navigation-next'),
      counterCurrent: this.root.querySelector('.pdp-swiper-gallery__counter-current'),
      counterTotal: this.root.querySelector('.pdp-swiper-gallery__counter-total'),
    };
    this.cacheSlides();
  }

  cacheSlides() {
    this.slides = Array.from(
      this.els.mediaList?.querySelectorAll(':scope > .pdp-swiper-gallery__main-slide[data-media-id]') || []
    ).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
  }

  getSlideCount() {
    return this.slides?.length || 0;
  }

  getSlides() {
    return this.slides || [];
  }

  destroySwipers() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
    this.unbindThumbsSwipeToMain();
    this.cacheSlides();
  }

  getSlideIndexByMediaId(mediaId) {
    return this.getSlides().findIndex((slide) => slide.dataset.mediaId === mediaId);
  }

  getThumbIndex(thumbEl) {
    if (!thumbEl || !this.els.thumbTrack) return -1;
    return Array.from(this.els.thumbTrack.children).indexOf(thumbEl);
  }

  goToSlide(index) {
    if (index < 0 || !this.mainSwiper) return;

    if (this.mainSwiper.params.loop) {
      this.mainSwiper.slideToLoop(index, 0);
    } else {
      this.mainSwiper.slideTo(index, 0);
    }
  }

  initSwipers() {
    if (!this.els.mainEl) return;

    this.cacheSlides();
    const slideCount = this.getSlideCount();
    if (slideCount === 0) return;

    const thumbSlideCount = this.els.thumbTrack?.children?.length || 0;
    const hasThumbs = thumbSlideCount > 1;
    const loop = slideCount > 1;

    if (this.els.counterTotal) {
      this.els.counterTotal.textContent = slideCount;
    }

    if (hasThumbs && this.els.thumbsEl) {
      this.thumbsSwiper = new Swiper(this.els.thumbsEl, {
        slidesPerView: 5,
        spaceBetween: 4,
        watchOverflow: true,
        allowTouchMove: thumbSlideCount > 5,
        simulateTouch: false,
        breakpoints: {
          750: {
            spaceBetween: 10,
          },
        },
      });
    }

    const canSwipe = slideCount > 1;

    const mainParams = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop,
      loopedSlides: loop ? slideCount : undefined,
      watchOverflow: true,
      ...this.getTrackpadSwipeParams(canSwipe),
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: {
        prevEl: this.els.navPrev,
        nextEl: this.els.navNext,
      },
      on: {
        init: (swiper) => this.onMainChange(swiper),
        slideChange: (swiper) => this.onMainChange(swiper),
        slideChangeTransitionEnd: (swiper) => this.syncThumbnails(swiper),
        transitionEnd: (swiper) => this.onTransitionEnd(swiper),
      },
    };

    this.mainSwiper = new Swiper(this.els.mainEl, mainParams);
    this.bindThumbClicks();
    if (hasThumbs) this.bindThumbsSwipeToMain();
    this.updateCounter();
    this.updateNavVisibility();
  }

  bindThumbsSwipeToMain() {
    if (!this.els.thumbsEl || !this.mainSwiper) return;

    const isFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!isFinePointer) return;

    this.unbindThumbsSwipeToMain();
    this._thumbsWheelLockUntil = 0;

    this._thumbsWheelHandler = (event) => {
      if (this.getSlideCount() <= 1) return;

      const delta = Math.abs(event.deltaX) >= Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (Math.abs(delta) < 30) return;

      const now = Date.now();
      if (now < this._thumbsWheelLockUntil) return;
      this._thumbsWheelLockUntil = now + 450;

      event.preventDefault();
      event.stopPropagation();

      if (delta > 0) this.mainSwiper.slideNext();
      else this.mainSwiper.slidePrev();
    };

    this.els.thumbsEl.addEventListener('wheel', this._thumbsWheelHandler, { passive: false });
  }

  unbindThumbsSwipeToMain() {
    if (!this.els.thumbsEl) return;

    if (this._thumbsWheelHandler) {
      this.els.thumbsEl.removeEventListener('wheel', this._thumbsWheelHandler);
      this._thumbsWheelHandler = null;
    }
  }

  bindThumbClicks() {
    if (!this.els.thumbTrack) return;

    if (this._thumbClickHandler) {
      this.els.thumbTrack.removeEventListener('click', this._thumbClickHandler);
    }

    this._thumbClickHandler = (event) => {
      const thumb = event.target.closest('.pdp-swiper-gallery__thumb');
      if (!thumb || !this.els.thumbTrack.contains(thumb)) return;

      event.preventDefault();
      const index = this.getThumbIndex(thumb);
      if (index === -1) return;

      this.goToSlide(index);
    };

    this.els.thumbTrack.addEventListener('click', this._thumbClickHandler);
  }

  onMainChange(swiper) {
    this.preventStickyHeader();
    this.updateCounter(swiper);
    this.updateNavVisibility();
    this.syncThumbnails(swiper);

    const slide = this.getActiveSlide(swiper);
    if (slide) {
      this.playActiveMedia(slide);
      this.announceLiveRegion(slide);
    }
  }

  onTransitionEnd(swiper) {
    const slide = this.getActiveSlide(swiper);
    if (slide) this.playActiveMedia(slide);
  }

  getActiveSlide(swiper = this.mainSwiper) {
    if (!swiper) return null;
    return this.getSlides()[this.getRealIndex(swiper)] || null;
  }

  setActiveMedia(mediaId, prepend) {
    if (prepend) {
      const slide = this.getSlides().find((item) => item.dataset.mediaId === mediaId);
      if (!slide || !this.els.mediaList) return;

      // A section-level first image stays pinned; variant media goes right after it
      const pinnedSlide = this.els.mediaList.querySelector(':scope > [data-pdp-pinned-first]');
      const leadSlide = pinnedSlide ? pinnedSlide.nextElementSibling : this.els.mediaList.firstElementChild;
      if (slide !== pinnedSlide && leadSlide !== slide) {
        this.els.mediaList.insertBefore(slide, leadSlide);
      }

      const thumb = this.els.thumbTrack?.querySelector(`[data-target="${mediaId}"]`);
      if (thumb && this.els.thumbTrack) {
        const pinnedThumb = this.els.thumbTrack.querySelector(':scope > [data-pdp-pinned-first]');
        const leadThumb = pinnedThumb ? pinnedThumb.nextElementSibling : this.els.thumbTrack.firstElementChild;
        if (thumb !== pinnedThumb && leadThumb !== thumb) {
          this.els.thumbTrack.insertBefore(thumb, leadThumb);
        }
      }

      this.refreshElements();
      return;
    }

    const index = this.getSlideIndexByMediaId(mediaId);
    if (index === -1) return;

    this.goToSlide(index);

    const slide = this.getSlides()[index];
    if (slide) {
      this.playActiveMedia(slide);
      this.announceLiveRegion(slide);
    }

    this.updateCounter();
    this.syncThumbnails();
  }

  getRealIndex(swiper = this.mainSwiper) {
    if (!swiper) return 0;
    return swiper.params.loop ? swiper.realIndex : swiper.activeIndex;
  }

  syncThumbnails(swiper = this.mainSwiper) {
    if (!this.els.thumbTrack || !swiper) return;

    const index = this.getRealIndex(swiper);
    const thumbs = Array.from(this.els.thumbTrack.children);

    thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);

      const btn = thumb.querySelector('.pdp-swiper-gallery__thumb-btn');
      if (!btn) return;
      if (isActive) btn.setAttribute('aria-current', 'true');
      else btn.removeAttribute('aria-current');
    });

    if (this.thumbsSwiper && index >= 0 && index < thumbs.length) {
      const slidesPerView = this.thumbsSwiper.params.slidesPerView;
      const maxIndex = Math.max(0, thumbs.length - slidesPerView);
      this.thumbsSwiper.slideTo(Math.min(index, maxIndex));
    }
  }

  updateCounter(swiper = this.mainSwiper) {
    if (!this.els.counterCurrent || !swiper) return;
    this.els.counterCurrent.textContent = this.getRealIndex(swiper) + 1;
  }

  updateNavVisibility() {
    const show = this.getSlideCount() > 1;
    [this.els.navPrev, this.els.navNext].forEach((btn) => {
      if (!btn) return;
      btn.style.display = show ? '' : 'none';
      btn.disabled = false;
    });
  }

  announceLiveRegion(slide) {
    const image = slide.querySelector('.pdp-swiper-gallery__image');
    const thumb = this.els.thumbTrack?.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
    const position = thumb?.dataset?.mediaPosition;
    if (!image || !position || !this.els.liveRegion) return;

    image.onload = () => {
      this.els.liveRegion.setAttribute('aria-hidden', false);
      this.els.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
      setTimeout(() => this.els.liveRegion.setAttribute('aria-hidden', true), 2000);
    };
    image.src = image.src;
  }

  playActiveMedia(slide) {
    window.pauseAllMedia?.();

    const video = slide.querySelector('video');
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }

  preventStickyHeader() {
    const header = document.querySelector('sticky-header');
    header?.dispatchEvent(new Event('preventHeaderReveal'));
  }

  setupAutoPlay() {
    setTimeout(() => {
      const slide = this.getActiveSlide() || this.getSlides()[0];
      if (slide) this.playActiveMedia(slide);
    }, 800);
  }
}

function getPdpSwiperGallery(root) {
  if (!root) return null;
  if (!root._pdpSwiperGallery) {
    root._pdpSwiperGallery = new PdpSwiperGallery(root);
    root._pdpSwiperGallery.bootstrap();
  }
  return root._pdpSwiperGallery;
}

function initPdpSwiperGalleries(scope = document) {
  scope.querySelectorAll('[data-pdp-swiper-gallery]').forEach(getPdpSwiperGallery);
}

window.PdpSwiperGallery = {
  get: getPdpSwiperGallery,
  init: initPdpSwiperGalleries,
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPdpSwiperGalleries());
} else {
  initPdpSwiperGalleries();
}
